// La table de marque (D-175, D-176 ; maquettes 4, 5, 6, 8, 8 bis, 9). Hors compétition FFBB.
//  - La table simplifiée en portrait (proposée d'office) : une moitié d'écran par équipe à ses couleurs, gros +1 +2 +3
//    (3x3 : +1, +2, LF), « Annuler » toujours visible, les fautes d'équipe en un geste puis « Pénalité » (3x3 : 6 points
//    puis « Bonus · 2 LF »), le quart-temps, le chrono en option (temps courant, ou rien), « Fin du match ».
//  - Le marqueur complet 5x5 en paysage : le numéro puis l'action, les fautes par joueuse (orange à 4, « Sort » à 5,
//    exclusion sur 2 T, 2 FL ou T + FL : règles FIBA 2026), les fautes d'équipe du QT, les temps-morts restants, la flèche
//    de possession, le chrono qui enchaîne pauses, mi-temps et prolongations, les 24 s / 14 s en option (éteintes).
//  - Le marqueur 3x3 en paysage : +1, +2, LF, −1, fautes en 6 points puis « Bonus », 12 s en option, « Ballon mort »,
//    temps-mort de 30 s, le journal en phrases, la question « Victoire de … 21–17 ? » qui bloque les nouveaux points.
//  - La table d'un match (un seul téléphone : il l'ouvre, la passe par QR, la rend) ou celle d'un entraînement (sur
//    l'appareil, rien de public). Hors réseau : tout reste sur le téléphone et part dans l'ordre au retour du réseau.
//  - L'affichage TV : la recopie plein écran du téléphone de la table (marche sans réseau). Aucun nom de joueuse.
import './table.css';
import { html, mount, openSheet, raw, toast, type Html } from './dom';
import { jersey, qrSvg } from './game-art';
import { alarm, engage, holdScreen, unlockAudio } from './drill-engine';
import { score, type Action, type ActionKind } from '../domain/boxscore';
import {
  PRESETS, afterAction, fmtClock, fmtShot, foulBadge, foulState, leftOf, mergeJournal, newId, newState, periodLabel, periodMs, playerFouls,
  presetOf, queueDel, quarterOf, readState, resetShot, sentence, setPeriod, stopClock, takeTimeout, teamFouls, tick, timeoutsLeft, timeoutsMax,
  isRunning, fixClock, isFix, withCommon, type Common,
  toggleClock, flip, type Op, type Rules, type Side, type TableState, type View,
} from '../domain/table';
import { agree, playerNoun, type TeamGender } from '../domain/labels';
import { TableError, matchBackend, tableClock, type TableBackend, type TablePlayer, type TableServer } from '../lib/table-api';
import { TableSender, deviceKey, dropLocal, linkEvent, loadLocal, saveLocal, saveName, savedName, setLinkEvent } from '../lib/table-store';

export interface TableTeam { name: string; short?: string; color: string }
export interface TableOpts {
  /** « match » : la table d'un match (la base, un seul téléphone) ; « local » : entraînement, tenue sur l'appareil. */
  kind: 'match' | 'local';
  eventId?: string;
  /** Le lien de saisie sans compte (D-057), s'il y en a un. */
  link?: string | null;
  /** « Match amical · 5x5 » (sous le titre). */
  kicker?: string;
  us: TableTeam; them: TableTeam;
  /** Le genre de l'équipe et le lieu ; sans eux (lien de saisie), ceux du match lus dans la base. */
  gender?: TeamGender; isHome?: boolean | null;
  players: TablePlayer[]; numbers: Record<string, number>;
  logo?: string | null;
  /** Le 3e mode de l'écran des stats en direct : revenir à « Stats » ou « Score seul ». */
  onMode?: (m: 'full' | 'score') => void;
  onClose: () => void;
  /** Après « Fin du match » : la feuille de match du club (staff connecté). */
  sheetHref?: string | null;
  /** Ouverture directe (entraînement, visite guidée). */
  preset?: string; view?: View;
  /** La base de la table : par défaut un match du club (table_*) ; un match de tournoi (D-177) a la sienne (tmatch_*). */
  backend?: TableBackend;
  /** Les règles de départ (un tournoi : celles de ses réglages) ; la vue « complète » proposée ou non (un match de tournoi
   *  en 5x5 n'a pas de joueuses nommées : la table simple seulement). */
  rules?: Rules; fullOk?: boolean;
  /** Qui accepte une demande de main (« le staff du match », « l’organisation ») ; le sous-titre de chaque équipe. */
  askWho?: string; subs?: { us: string; them: string };
  /** Après « Fin du match », la phrase de la fin (un tournoi : les capitaines ont 5 min). */
  doneText?: string;
  /** La vue choisie à l'ouverture (un tournoi la garde pour le terrain la journée). */
  onView?: (v: View) => void;
}

const PALETTE = ['#15803D', '#DC2626', '#1D4ED8', '#7C3AED', '#EA580C', '#0F766E', '#111827', '#DB2777'];
/** Le nom de chaque couleur, et l'équipe qui va avec : à l'entraînement, « Les Bleus » passés en vert deviennent « Les Verts ». */
const COLOR_LABEL: Record<string, [string, string]> = {
  '#15803D': ['vert', 'Les Verts'], '#DC2626': ['rouge', 'Les Rouges'], '#1D4ED8': ['bleu', 'Les Bleus'], '#7C3AED': ['violet', 'Les Violets'],
  '#EA580C': ['orange', 'Les Orange'], '#0F766E': ['turquoise', 'Les Turquoise'], '#111827': ['noir', 'Les Noirs'], '#DB2777': ['rose', 'Les Roses'],
};
const COLOR_TEAMS = new Set(Object.values(COLOR_LABEL).map(x => x[1]));
/** Encre lisible sur une couleur (blanc ou presque noir). */
export function inkOn(hex: string): string {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return '#FFFFFF';
  const [r, g, b] = [m[1], m[2], m[3]].map(x => parseInt(x!, 16) / 255).map(c => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.45 ? '#111111' : '#FFFFFF';
}
const rgb = (h: string) => { const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(h); return m ? [1, 2, 3].map(i => parseInt(m[i]!, 16)) : [0, 0, 0]; };
/** La couleur de l'adversaire : jamais trop proche de la nôtre (deux verts, deux bleus) : alors rouge, ou bleu. */
export function themColor(us: string, wanted: string): string {
  const near = (a: string, b: string) => { const [x, y] = [rgb(a), rgb(b)]; return Math.hypot(x[0]! - y[0]!, x[1]! - y[1]!, x[2]! - y[2]!) < 140; };
  if (!near(us, wanted)) return wanted;
  return near(us, '#DC2626') ? '#1D4ED8' : '#DC2626';
}
const ic = (k: string) => raw(`<span class="mq-i mq-i-${k}" aria-hidden="true"></span>`);
const pips = (n: number, max: number, count: number | null = null) =>
  html`<span class="mq-pips">${Array.from({ length: max }, (_, i) => html`<i class="${i < n ? 'on' : ''}"></i>`)}${count !== null ? html`<em>${count}</em>` : ''}</span>`;
const ffbb = (cls = '') => html`<span class="mq-ffbb ${cls}">Hors compétition FFBB</span>`;
/** Les règles d'un tournoi en une ligne (« 3x3 · 10 min ou 21 points · pas de 12 s »). */
const rulesLine = (r: Rules) => (r.format === '3x3' ? `3x3 · ${r.periodMin} min ou ${r.target ?? 21} points · pas de ${r.shot} s`
  : `${r.periods > 1 ? `${r.periods} × ` : ''}${r.periodMin} min · ${r.clock ? 'chrono' : 'sans chrono'} · pas de ${r.shot} s`);
const vib = () => { try { navigator.vibrate?.(15); } catch { /* sans vibreur */ } };
/** « de Rivebelle 1 », « des Bleus », « d’Aureilhan ». */
export const deName = (n: string) => (/^les\s/i.test(n) ? `des ${n.slice(4)}` : /^le\s/i.test(n) ? `du ${n.slice(3)}` : /^[aeiouyhéèâîô]/i.test(n) ? `d’${n}` : `de ${n}`);

type Screen = 'loading' | 'setup' | 'other' | 'board' | 'pass' | 'lost' | 'done' | 'closed' | 'tv';
type Sel = { side: 'us'; id: string } | { side: 'them'; num: number } | null;

/** Ouvre la table de marque en plein écran ; rend de quoi la fermer. */
export function openTable(o: TableOpts): () => void {
  unlockAudio();
  const host = document.createElement('div');
  host.className = 'mq-full';
  host.setAttribute('role', 'dialog');
  host.setAttribute('aria-label', 'Table de marque');
  document.body.append(host);
  // Écran allumé, parade iOS, plein écran (D-098) ; pendant une visite guidée, l'écran allumé seulement : le plein écran
  // du navigateur cacherait la bulle du guide.
  const release = document.querySelector('.gd-layer') ? holdScreen() : engage(host, 'Table de marque');
  const id = o.kind === 'local' ? 'local' : o.eventId!;
  const key = deviceKey();
  const api: TableBackend = o.backend ?? matchBackend(id, o.link ?? null);
  const askWho = o.askWho ?? 'le staff du match';
  const fullOk = o.fullOk !== false;
  if (o.kind === 'match' && o.link) setLinkEvent(o.link, id);

  let screen: Screen = 'loading';
  let back: Screen = 'board';                     // l'écran d'où vient la TV
  let srv: TableServer | null = null;
  let actions: Action[] = [];
  let queue: Op[] = [];
  /** L'heure de la base, estimée sur ce téléphone (ClockSync) : tous les chronos de la table la suivent. */
  const T = () => tableClock.now();
  let st: TableState = newState(o.preset ?? 'fiba5', T(), { view: o.view ?? 'simple', rules: o.rules });
  let sel: Sel = null;
  let players = o.players, numbers = o.numbers;
  let message = '';
  let asking = false;
  let passToken: string | null = null, passUntil = 0;
  let endArmed = 0;
  let setup = { preset: o.preset ?? 'fiba5', view: (o.view ?? 'simple') as View, name: savedName() };
  let poll = 0, ticker = 0, beat = 0, closed = false;

  /** De quoi rouvrir cette table sans réseau (après un rechargement, la base injoignable). */
  const ctxOf = () => (o.kind === 'match' ? { us: o.us, them: o.them, kicker: o.kicker, gender: o.gender, isHome: o.isHome, players, numbers, logo: o.logo ?? null, link: o.link ?? null } : undefined);
  const persist = () => { if (screen === 'board' || screen === 'done' || screen === 'tv' || screen === 'pass') saveLocal(id, { actions, queue, state: st, ctx: ctxOf(), offset: tableClock.offset }); };
  const sender = o.kind === 'match' ? new TableSender((ops, state) => api.sync(key, ops, state), () => ({ queue, state: st }), (sent, skipped) => {
    queue = queue.filter(x => !sent.includes(x));
    if (skipped.length) { actions = actions.filter(a => !skipped.includes(a.id)); toast(`${skipped.length} action(s) refusée(s) par la base (retirée(s) du journal)`, 'ko'); }
    persist(); paintNet();
    if (screen === 'done') draw();
  }, kind => {
    if (kind === 'denied') { lose('Un autre téléphone tient maintenant la table de ce match.'); return; }
    if (kind === 'closed' && !st.finished) { message = 'Match terminé : la table est fermée. Le score est enregistré.'; }
    paintNet();
  }) : null;
  const kick = (d = 250) => sender?.kick(d);

  const close = () => {
    if (closed) return;
    closed = true;
    persist();
    clearInterval(ticker); clearInterval(poll); clearInterval(beat);
    sender?.stop();
    window.removeEventListener('hashchange', onHash);
    release();
    host.remove();
  };
  const onHash = () => { close(); };
  window.addEventListener('hashchange', onHash);

  // --- Les données dérivées ----------------------------------------------------------------------------------------------
  const r = () => st.rules;
  const nameOf = (s: Side) => (s === 'us' ? st.names.us ?? o.us.name : st.names.them ?? o.them.name);
  const shortOf = (s: Side) => (s === 'us' ? st.names.us ?? o.us.short ?? o.us.name : st.names.them ?? o.them.short ?? o.them.name);
  const colorOf = (s: Side) => {
    if (s === 'us') return st.colors.us ?? o.us.color;
    return themColor(st.colors.us ?? o.us.color, st.colors.them ?? o.them.color);
  };
  const style = (s: Side) => `--c:${colorOf(s)};--ci:${inkOn(colorOf(s))}`;
  const numberOf = (pid: string) => numbers[pid] ?? players.find(p => p.person_id === pid)?.jersey ?? null;
  const person = (pid: string) => ({ num: numberOf(pid), name: players.find(p => p.person_id === pid)?.name ?? null });
  const subOf = (s: Side) => (o.subs ? o.subs[s] : o.kind === 'local' ? '' : s === 'us'
    ? (o.isHome === true ? 'Nous · à domicile' : o.isHome === false ? 'Nous · à l’extérieur' : 'Nous')
    : (o.isHome === true ? agree(o.gender ?? 'X', 'Les visiteurs', 'Les visiteuses') : o.isHome === false ? agree(o.gender ?? 'X', 'Les locaux', 'Les locales') : 'Adversaire'));
  const kicker = (v: View) => `${o.kicker ?? (o.kind === 'local' ? 'Entraînement' : 'Match')} · ${r().format} · ${v === 'simple' ? 'table simplifiée' : r().format === '3x3' ? 'marqueur 3x3' : 'marqueur complet'}`;
  const blocked = () => !!st.win || st.finished;
  const clockLeft = (now = T()) => leftOf(st.phase === 'break' && st.brk ? st.brk : st.clock, now);
  const brkLabel = () => (st.brk && st.brk.dur > 5 * 60_000 ? 'Mi-temps' : 'Pause');
  const oppNums = () => [...new Set([...st.oppNums, ...actions.flatMap(a => (a.opp_number !== null && a.opp_number !== undefined ? [a.opp_number] : []))])].sort((a, b) => a - b);

  // --- Les gestes ---------------------------------------------------------------------------------------------------------
  const add = (kind: ActionKind, x: { person_id?: string | null; opp_number?: number | null } = {}) => {
    if (blocked() && kind !== 'us_to' && kind !== 'opp_to') return;
    const now = T();
    const a: Action = { id: newId(), person_id: x.person_id ?? null, kind, quarter: quarterOf(st.period), created_at: new Date().toISOString(),
      opp_number: x.opp_number ?? null, game_ms: r().clock ? Math.round(leftOf(st.clock, now)) : null };
    actions = [...actions, a];
    queue = [...queue, { op: 'add', a }];
    st = afterAction(st, actions, kind, now);
    vib(); persist(); kick(); draw();
  };
  /** Une correction du chrono (D-176 : chaque correction va au journal et sur la feuille du club). */
  const addFix = (f: { kind: 'clock_fix' | 'shot_fix'; game_ms: number; meta: Record<string, number> }) => {
    const a: Action = { id: newId(), person_id: null, kind: f.kind, quarter: quarterOf(st.period), created_at: new Date().toISOString(), opp_number: null, game_ms: f.game_ms, meta: f.meta };
    actions = [...actions, a];
    queue = [...queue, { op: 'add', a }];
  };
  const undo = () => {
    // « Annuler » : la dernière action du match (une correction du chrono se corrige dans le chrono, elle reste au journal).
    const idx = actions.map(a => isFix(a.kind)).lastIndexOf(false);
    const last = idx >= 0 ? actions[idx] : undefined;
    if (!last) return;
    actions = actions.filter((_, i) => i !== idx);
    queue = sender?.inflight.has(last.id) ? [...queue, { op: 'del', id: last.id }] : queueDel(queue, last.id);
    st = { ...st, win: null };
    persist(); kick(); draw();
    const s = sentence(last, [...actions, last], { us: shortOf('us'), them: shortOf('them'), person }, r());
    toast(`Annulé : ${s.who} · ${s.what}`);
  };
  const change = (next: TableState, send = true) => { st = next; persist(); if (send) kick(400); draw(); };
  const finish = () => {
    st = { ...st, finished: true, win: null, clock: stopClock(st.clock, T()), shot: stopClock(st.shot, T()) };
    if (o.kind === 'match') queue = [...queue, { op: 'finish' }];
    screen = 'done';
    persist(); kick(0); draw();
  };
  const lose = (why: string) => {
    if (screen === 'lost') return;
    message = why;
    screen = 'lost';
    clearInterval(poll);
    draw();
  };

  // --- Le serveur (table d'un match) -----------------------------------------------------------------------------------
  const adopt = (s: TableServer, fresh: boolean) => {
    srv = s;
    players = s.players?.length ? s.players : players;
    numbers = s.match?.numbers ?? numbers;
    const local = loadLocal(id);
    if (local && !fresh) {
      // Ce téléphone tenait déjà la table : il fait foi ; on ajoute seulement ce que d'autres ont saisi (mode Stats).
      st = local.state; queue = local.queue;
      actions = mergeJournal(s.actions, local.queue.flatMap(x => (x.op === 'add' ? [x.a] : [])), local.queue);
    } else {
      // Les chronos sont à l'heure de la base : l'état d'un autre téléphone se reprend tel quel (« Passer la main »).
      st = readState(s.state, T()) ?? st;
      queue = [];
      actions = s.actions;
    }
    st = withCommon(st, (s.common as Common | null) ?? null);
    screen = 'board';
    persist();
    if (queue.length) kick(0);           // ce qui attendait (hors réseau, avant un rechargement) part tout de suite
    startPoll(20_000);
  };
  const load = async () => {
    if (o.kind === 'local') {
      const l = loadLocal('local');
      if (l && !o.preset) { st = l.state; actions = l.actions; queue = []; screen = 'board'; }
      else if (o.preset) { st = newState(o.preset, T(), { view: o.view ?? 'simple' }); actions = []; screen = 'board'; persist(); }
      else screen = 'setup';
      draw();
      return;
    }
    try {
      const s = await api.state(key);
      srv = s;
      const m = s.match;
      if (m) {
        if (!o.us.color) o.us.color = m.color ?? '#1D4ED8';
        if (o.logo === undefined) o.logo = m.logo;
        if (o.isHome === undefined) o.isHome = m.is_home;
        if (!o.gender) o.gender = m.gender;
        if (!o.kicker) o.kicker = m.competition === 'amical' ? 'Match amical' : 'Match';
      }
      if (s.mine) adopt(s, false);
      else if (!s.window && !loadLocal(id)) { screen = 'closed'; message = s.match?.live === false && s.actions.length ? 'Match terminé : la table est fermée.' : 'La table s’ouvre le jour du match, jusqu’au score final.'; }
      else if (s.held) { screen = 'other'; asking = s.asking; startPoll(4000); }
      else if (loadLocal(id)) {
        // Rendue par le staff pendant que ce téléphone était hors réseau : il la reprend s'il est le premier.
        const l = loadLocal(id)!;
        const again = await api.open(key, savedName() || null, l.state).catch(() => null);
        if (again?.mine) adopt(again, false); else { screen = 'setup'; }
      } else screen = 'setup';
    } catch (e) {
      // Hors réseau : la copie de ce téléphone suffit pour continuer.
      const l = loadLocal(id);
      if (l?.offset !== undefined && !tableClock.measured) tableClock.offset = l.offset;   // l'écart d'horloge mesuré avant la coupure
      if (l && !(e instanceof TableError && e.kind === 'denied')) { st = l.state; actions = l.actions; queue = l.queue; screen = 'board'; if (sender) sender.offline = true; kick(5000); }
      else { screen = 'closed'; message = e instanceof TableError && e.kind === 'denied' ? 'Cette table est réservée à la saisie du match.' : 'Pas de réseau : la table s’ouvre une première fois avec le réseau.'; }
    }
    draw();
  };
  const startPoll = (ms: number) => {
    clearInterval(poll);
    if (o.kind !== 'match') return;
    poll = window.setInterval(() => void refresh(), ms);
  };
  const refresh = async () => {
    if (closed || o.kind !== 'match' || sender?.pending) return;
    try {
      const s = await api.state(key);
      srv = s;
      if (sender) sender.offline = false;
      if (screen === 'other') {
        asking = s.asking;
        if (s.mine) { adopt(s, true); toast(o.backend ? 'L’organisation t’a donné la main : tu tiens la table' : 'Le staff t’a donné la main : tu tiens la table'); }
        else if (!s.held) { screen = 'setup'; clearInterval(poll); }
        draw();
        return;
      }
      if (screen === 'pass' && !s.mine) { lose('C’est fait : la table est passée sur l’autre téléphone. Le match continue là-bas, journal compris.'); return; }
      if ((screen === 'board' || screen === 'tv') && !s.mine) { lose(o.backend ? 'Un autre téléphone tient maintenant la table de ce terrain (main donnée par l’organisation, ou terrain libéré).' : 'Un autre téléphone tient maintenant la table de ce match (main donnée par le staff, ou table libérée).'); return; }
      if (screen === 'board' && !queue.length) {
        const before = actions.length;
        actions = mergeJournal(s.actions, [], queue);
        if (actions.length !== before) { persist(); draw(); }
      }
      // Une horloge commune (lot 3 : les terrains d'un tournoi) pilote cette table, ou ne la pilote plus.
      const common = (s.common as Common | null) ?? null;
      if (!!common !== !!st.common || (common && JSON.stringify(common.clock) !== JSON.stringify(st.clock))) { st = withCommon(st, common); persist(); draw(); }
      paintNet();
    } catch { if (sender) sender.offline = true; paintNet(); }
  };

  // --- Le chrono ------------------------------------------------------------------------------------------------------------
  const paintClocks = () => {
    const now = T();
    for (const el of host.querySelectorAll<HTMLElement>('[data-clk]')) el.textContent = fmtClock(clockLeft(now));
    for (const el of host.querySelectorAll<HTMLElement>('[data-shot]')) el.textContent = fmtShot(leftOf(st.shot, now));
    for (const el of host.querySelectorAll<HTMLElement>('[data-to]')) {
      const left = st.to ? Math.max(0, st.to.until - now) : 0;
      el.textContent = st.to && el.dataset.to === st.to.side ? `Temps-mort · ${Math.ceil(left / 1000)} s` : el.dataset.idle ?? '';
    }
    if (screen === 'pass') {
      const s = Math.max(0, Math.ceil((passUntil - Date.now()) / 1000));
      const el = host.querySelector<HTMLElement>('[data-passleft]');
      if (el) el.textContent = s ? `Valable encore ${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')} · un seul scan` : 'QR expiré : « Nouveau QR »';
    }
  };
  // Un signe de vie toutes les 30 s (l'état et le chrono) : l'organisatrice d'un tournoi voit qu'une table est hors réseau.
  beat = window.setInterval(() => { if (sender && !sender.pending && (screen === 'board' || screen === 'tv')) kick(0); }, 30_000);
  ticker = window.setInterval(() => {
    if (screen !== 'board' && screen !== 'tv' && screen !== 'pass') { paintClocks(); return; }
    const before = st;
    const t = tick(st, actions, T());
    if (t.s !== before) {
      st = t.s;
      persist(); kick(400);
      if (t.ring) alarm();
      draw();
      return;
    }
    paintClocks();
  }, 200);

  // --- Le réseau ------------------------------------------------------------------------------------------------------------
  const netChip = (): Html => {
    if (o.kind === 'local') return html`<span class="mq-chip"><i></i>Sur ce téléphone</span>`;
    const n = queue.length;
    const off = sender?.offline || (typeof navigator !== 'undefined' && navigator.onLine === false);
    if (n && off) return html`<span class="mq-chip off" data-net><i></i>Hors réseau · ${n} action${n > 1 ? 's' : ''} en attente</span>`;
    if (off) return html`<span class="mq-chip off" data-net><i></i>Hors réseau</span>`;
    if (srv?.match?.live || actions.length) return html`<span class="mq-chip" data-net><i></i>En direct</span>`;
    return html`<span class="mq-chip" data-net><i></i>Relié</span>`;
  };
  const paintNet = () => { for (const el of host.querySelectorAll<HTMLElement>('[data-net]')) el.outerHTML = netChip().s; };

  // --- Les écrans -----------------------------------------------------------------------------------------------------------
  const titleOf = () => `${nameOf('us')} – ${nameOf('them')}`;
  const logo = () => (o.logo ? html`<img class="lg" src="${o.logo}" alt="">` : html`<span class="lg">${(o.us.name || 'A').charAt(0)}</span>`);
  const telHead = (sub: string) => html`<div class="mq-telh">${logo()}<span><b>${o.kind === 'local' ? 'Table de marque' : titleOf()}</b><small>${sub}</small></span>${netChip()}</div>`;
  const clockBox = (cls: string, label: string): Html => {
    const brk = st.phase === 'break';
    const lab = st.common ? `Chrono commun · géré par ${st.common.by}` : brk ? brkLabel() : st.phase === 'end' ? 'Temps réglementaire fini' : label;
    // Chrono arrêté : un toucher sur le temps pour le corriger (D-176) ; jamais une horloge commune.
    const editable = !st.common && !isRunning(st.clock) && st.phase !== 'break';
    return html`<b class="${cls} ${brk ? 'mq-brk' : ''} ${editable ? 'mq-edit' : ''}" data-clk ${editable ? html`data-a="cedit" role="button" tabindex="0" aria-label="Corriger le chrono"` : ''}>${fmtClock(clockLeft())}</b><small>${lab}</small>`;
  };

  const drawSetup = (): Html => {
    const p = presetOf(setup.preset);
    const three = (o.rules ?? p.rules).format === '3x3';
    return html`<div class="mq-tel-in">
      ${telHead(o.kind === 'local' ? 'Entraînement · tenue sur ce téléphone' : 'Table de marque')}
      <div class="mq-next" data-guide="mq-setup">
        <span class="k">${o.kicker ?? 'Match'}</span>
        <div class="mq-vs"><div>${raw(jersey({ color: colorOf('us'), ink: inkOn(colorOf('us')), letter: shortOf('us').charAt(0).toUpperCase() }, 46))}<b>${nameOf('us')}</b></div><span>–</span>
          <div>${raw(jersey({ color: colorOf('them'), ink: inkOn(colorOf('them')), letter: shortOf('them').charAt(0).toUpperCase() }, 46))}<b>${nameOf('them')}</b></div></div>
        ${o.rules ? html`<p class="sub">${rulesLine(o.rules)}</p>` : html`<p class="sub">${p.rules.format} · ${p.sub}${p.rules.format === '3x3' ? ' · pas de 12 s' : ' · pas de 24 s'}</p>
        <p class="mq-lab">Les règles · réglables ensuite</p>
        <div class="mq-presets" role="radiogroup" aria-label="Les règles">${PRESETS.map(x => html`<button type="button" role="radio" data-a="preset" data-p="${x.id}" aria-pressed="${String(x.id === setup.preset)}"><b>${x.label}</b><small>${x.sub}</small></button>`)}</div>`}
        ${fullOk ? html`<p class="mq-lab">La table</p>
        <div class="seg" role="radiogroup" aria-label="La table">
          <button type="button" role="radio" data-a="sview" data-v="simple" aria-pressed="${String(setup.view === 'simple')}">Table simple<small>portrait, d’une main</small></button>
          <button type="button" role="radio" data-a="sview" data-v="full" aria-pressed="${String(setup.view === 'full')}">${three ? 'Marqueur 3x3' : 'Marqueur complet'}<small>téléphone en paysage</small></button></div>` : ''}
        ${o.kind === 'match' ? html`<label class="mq-name"><span>Ton prénom (facultatif, oublié ce soir)</span><input id="mq-name" maxlength="24" autocomplete="given-name" value="${setup.name}" placeholder="ex. Léa"></label>` : ''}
        <button type="button" class="mq-open" data-a="open">${ic('play')} Ouvrir la table</button>
      </div>
      <p class="mq-note">${o.kind === 'match' ? (o.backend ? 'Ton téléphone tient la table de ce terrain. ' : 'Un seul téléphone tient la table de ce match : le tien, dès que tu l’ouvres. ') : 'Rien n’est envoyé : le score reste sur ce téléphone. '}L’écran reste allumé. Sans réseau, la table continue${o.kind === 'match' ? ' : tout part au retour du réseau.' : '.'}</p>
      <span class="mq-center">${ffbb()}</span>
    </div>`;
  };

  const drawOther = (): Html => html`<div class="mq-tel-in">
      ${telHead('Table de marque')}
      <div class="mq-ok warn"><i>${ic('hand')}</i><span><b>Tenue par un autre téléphone</b>
        <small>${srv?.holder_name ? `Le téléphone de ${srv.holder_name} tient la table de ce match.` : 'Un autre téléphone tient la table de ce match.'} Un seul téléphone saisit : demande la main, ${o.backend ? 'l’organisation accepte dans « Tables »' : 'le staff accepte depuis la fiche du match'}.</small></span></div>
      ${asking ? html`<div class="mq-next"><span class="k">Demande envoyée</span><p class="sub">${o.backend ? 'En attente de l’organisation. Dès qu’elle accepte' : 'En attente du staff du match. Dès qu’il accepte'}, ce téléphone devient la table ; le match continue avec son journal.</p></div>`
        : html`<div class="mq-next"><label class="mq-name"><span>Ton prénom (facultatif, oublié ce soir)</span><input id="mq-name" maxlength="24" autocomplete="given-name" value="${setup.name}" placeholder="ex. Hugo"></label>
          <button type="button" class="mq-open" data-a="ask">${ic('hand')} Demander la main</button></div>`}
      ${o.onMode ? html`<button type="button" class="mq-ghost" data-a="mode" data-m="score">Score seul, sans la table</button>` : ''}
      <button type="button" class="mq-ghost" data-a="back">Fermer</button>
      <span class="mq-center">${ffbb()}</span></div>`;

  const drawLost = (): Html => html`<div class="mq-tel-in">
      ${telHead('Table de marque')}
      <div class="mq-ok warn"><i>${ic('hand')}</i><span><b>Tu ne tiens plus la table</b><small>${message}</small></span></div>
      ${queue.filter(x => x.op === 'add').length ? html`<p class="mq-note">${queue.filter(x => x.op === 'add').length} action(s) de ce téléphone n’étaient pas parties : elles restent sur ce téléphone, signale-les au staff.</p>` : ''}
      <button type="button" class="mq-ghost" data-a="back">Fermer</button>
      <span class="mq-center">${ffbb()}</span></div>`;

  const drawClosed = (): Html => html`<div class="mq-tel-in">
      ${telHead('Table de marque')}
      <div class="mq-ok warn"><i>${ic('clock')}</i><span><b>Table fermée</b><small>${message}</small></span></div>
      ${o.onMode ? html`<button type="button" class="mq-ghost" data-a="mode" data-m="full">Revenir aux stats</button>` : ''}
      <button type="button" class="mq-ghost" data-a="back">Fermer</button></div>`;

  const drawDone = (): Html => {
    const sc = score(actions);
    const pending = queue.length;
    return html`<div class="mq-tel-in">
      ${telHead('Table de marque')}
      <div class="mq-ok ${pending && o.kind === 'match' ? 'warn' : ''}"><i>${ic('check')}</i><span><b>Match terminé</b>
        <small>${o.kind === 'local' ? 'Rien n’est enregistré : le score reste sur ce téléphone.' : pending ? 'Le score final partira au retour du réseau : garde cette page ouverte.' : o.doneText ?? 'Score final enregistré. La feuille de stats et le score public sont à jour.'}</small></span></div>
      <div class="mq-cur"><span class="t">${shortOf('us')}</span><b>${sc.us}</b><span class="s">–</span><b>${sc.them}</b><span class="t">${shortOf('them')}</span></div>
      ${o.sheetHref && !pending ? html`<a class="mq-open" href="${o.sheetHref}" data-a="sheet">Feuille de match du club</a>` : ''}
      ${o.kind === 'local' ? html`<button type="button" class="mq-open" data-a="new">Nouveau match</button>` : ''}
      <button type="button" class="mq-ghost" data-a="back">Fermer</button>
      <span class="mq-center">${ffbb()}</span></div>`;
  };

  const drawPass = (): Html => {
    const sc = score(actions);
    const url = passToken ? api.passUrl(passToken) : '';
    return html`<div class="mq-tel-in">
      ${telHead('Table de marque')}
      <div class="mq-pass" data-guide="mq-pass"><h3>Passer la main</h3>
        <p>Le prochain bénévole scanne ce QR avec l’appareil photo : <b>la table passe sur son téléphone</b>, le match continue où il en est, journal compris.</p>
        ${passToken ? html`<span class="qr">${raw(qrSvg(url, 'QR « Passer la main »'))}</span>` : html`<p>Préparation du QR…</p>`}
        <span class="meta" data-passleft>Valable 2 min · un seul scan</span>
        ${passToken && passUntil < Date.now() ? html`<button type="button" class="mq-btn" data-a="pass">Nouveau QR</button>` : ''}</div>
      <div class="mq-cur"><span class="t">${shortOf('us')}</span><b>${sc.us}</b><span class="s">–</span><b>${sc.them}</b><span class="t">${shortOf('them')}</span></div>
      <p class="mq-note mq-center" style="text-align:center">${periodLabel(r(), st.period)}${r().clock ? ` · ${fmtClock(clockLeft())} à jouer` : ''}. Le chrono, les fautes et le journal passent avec le match. Dès que le QR est scanné, ton téléphone n’est plus la table.</p>
      <button type="button" class="mq-ghost" data-a="board">Annuler</button>
      <span class="mq-center">${ffbb()}</span></div>`;
  };

  // 4 · La table simplifiée, en portrait
  const drawSimple = (): Html => {
    const R = r();
    const half = (side: Side) => {
      const tf = teamFouls(actions, R, st.period, side), badge = foulBadge(R, tf);
      const max = R.teamFouls;
      const pts: [ActionKind, string][] = R.format === '3x3'
        ? (side === 'us' ? [['us1', '+1'], ['us2', '+2'], ['us_ft', 'LF']] : [['opp1', '+1'], ['opp2', '+2'], ['opp_ft', 'LF']])
        : (side === 'us' ? [['us1', '+1'], ['us2', '+2'], ['us3', '+3']] : [['opp1', '+1'], ['opp2', '+2'], ['opp3', '+3']]);
      return html`<section class="mq-half" style="${style(side)}">
        <div class="mq-ht"><span><b>${nameOf(side)}</b><small>${subOf(side)}</small></span>
          <button type="button" class="mq-tf" data-a="tf" data-side="${side}" ${side === 'us' ? html`data-guide="mq-tf"` : ''} aria-label="Une faute d’équipe de plus : ${nameOf(side)}">
            <span>Fautes d’équipe${R.format === '5x5' ? ` · ${periodLabel(R, Math.min(st.period, R.periods))}` : ''}</span>${pips(Math.min(tf, max), max, tf)}${badge ? html`<span class="mq-pen">${badge}</span>` : ''}</button></div>
        <div class="mq-score" aria-live="polite">${score(actions)[side]}</div>
        <div class="mq-pts" ${side === 'us' ? html`data-guide="mq-pts"` : ''}>${pts.map(([k, l]) => html`<button type="button" data-a="pt" data-k="${k}" ${blocked() ? 'disabled' : ''} aria-label="${nameOf(side)} ${l}">${l}</button>`)}</div></section>`;
    };
    const timed = R.clock;
    return html`<div class="mq-simple">
      <header class="mq-sh"><button type="button" class="mq-btn ico" data-a="back" aria-label="Fermer la table">‹</button>
        <span><b>${titleOf()}</b><small>${kicker('simple')}</small></span>
        <button type="button" class="mq-btn ico" data-a="set" aria-label="Réglages de la table" data-guide="mq-set">${ic('gear')}</button></header>
      <div class="mq-band">${ffbb()}<span>${st.common ? 'Chrono commun' : timed ? 'Chrono : temps courant' : 'Sans chrono'}</span></div>
      ${half('us')}
      <div class="mq-mid">
        <button type="button" class="mq-btn" data-a="undo" ${actions.length ? '' : 'disabled'} data-guide="mq-undo">${ic('undo')} Annuler</button>
        <div class="mq-qt" aria-label="Période">${st.common ? '' : html`<button type="button" data-a="qt" data-d="-1" aria-label="Période précédente">‹</button>`}<b>${periodLabel(R, st.period)}</b>${st.common ? '' : html`<button type="button" data-a="qt" data-d="1" aria-label="Période suivante">›</button>`}</div>
        ${timed ? html`<div class="mq-clk ${st.common ? 'common' : ''}" data-guide="mq-clock">${clockBox('', isRunning(st.clock) ? 'temps courant' : 'arrêté')}
          ${st.common ? '' : html`<button type="button" class="mq-btn ico" data-a="clk" aria-label="${isRunning(st.clock) ? 'Arrêter le chrono' : 'Lancer le chrono'}">${ic(isRunning(st.clock) ? 'pause' : 'play')}</button>`}</div>`
          : html`<div class="mq-clk none"><b>Sans chrono</b></div>`}
        ${st.shotOn && !st.common ? html`<div class="mq-possrow"><b data-shot>${fmtShot(leftOf(st.shot, T()))}</b>
          <button type="button" class="mq-poss" data-a="sr" data-s="${R.shot}" aria-label="Changement de possession : remettre les ${R.shot} s">↻ Possession · ${R.shot} s</button>
          ${R.shotShort ? html`<button type="button" class="mq-poss alt" data-a="sr" data-s="${R.shotShort}" aria-label="Remettre ${R.shotShort} s">${R.shotShort} s</button>` : ''}</div>` : ''}
      </div>
      ${half('them')}
      ${st.win ? winBar() : html`<footer class="mq-foot">${netChip()}<button type="button" class="mq-btn end" data-a="end">${endArmed > Date.now() ? `Confirmer ? ${score(actions).us}–${score(actions).them}` : 'Fin du match'}</button></footer>`}
    </div>`;
  };

  const modeSeg = (): Html => (o.onMode ? html`<div class="seg" role="radiogroup" aria-label="Saisie" data-guide="mq-modes">
      <button type="button" role="radio" data-a="mode" data-m="full" aria-pressed="false">Stats</button>
      <button type="button" role="radio" data-a="mode" data-m="score" aria-pressed="false">Score seul</button>
      <button type="button" role="radio" aria-pressed="true">Table de marque</button></div>`
    : html`<span class="ttl"><b>${titleOf()}</b><small>${kicker('full')}</small></span>`);
  const landTop = (): Html => html`<header class="mq-lt"><button type="button" class="mq-btn ico" data-a="back" aria-label="Fermer la table">‹</button>
      ${modeSeg()}${netChip()}${ffbb()}<button type="button" class="mq-btn ico" data-a="set" aria-label="Réglages de la table" data-guide="mq-set">${ic('gear')}</button></header>
    <p class="mq-turn">${ic('clock')} Plus confortable téléphone couché (paysage).</p>`;

  // 5 · Le marqueur complet 5x5, en paysage
  const drawFull5 = (): Html => {
    const R = r(), now = T(), sc = score(actions);
    const toLeft = (s: Side) => timeoutsLeft(actions, R, st.period, s, leftOf(st.clock, now));
    const toMax = timeoutsMax(R, st.period);
    const sideHead = (s: Side) => {
      const tf = teamFouls(actions, R, st.period, s), pen = foulState(R, tf) !== 'none';
      return html`<div class="mq-sideh"><b>${shortOf(s)}</b><span class="r"><span class="mq-mini ${pen ? 'pen' : ''}">Fautes ${tf}${pen ? ' · Pénalité' : ''}</span>
        <span class="mq-mini" aria-label="${toLeft(s)} temps-morts restants">TM ${Array.from({ length: toMax }, (_, i) => html`<i class="${i < toLeft(s) ? 'on' : ''}"></i>`)}</span></span></div>`;
    };
    const tile = (num: number | null, name: string | null, key: string, isSel: boolean, s: Side, data: Html) => {
      const f = playerFouls(actions, key, R);
      const cls = f.out || f.gd ? 'f5' : f.total === (R.playerFouls ?? 5) - 1 ? 'f4' : '';
      const max = R.playerFouls ?? 5;
      return html`<button type="button" class="mq-p ${cls} ${isSel ? 'sel' : ''} ${name ? '' : 'nameless'}" style="--c:${colorOf(s)}" data-a="pick" ${data}>
        <b>${num ?? '–'}</b>${name ? html`<span>${name}</span>` : ''}${f.gd ? html`<span class="out">${agree(o.gender ?? 'X', 'Exclu', 'Exclue')}</span>` : f.out ? html`<span class="out">${f.total} · Sort</span>`
          : html`<span class="f">${Array.from({ length: max }, (_, i) => html`<i class="${i < f.total ? 'on' : ''}"></i>`)}</span>`}</button>`;
    };
    const ours = [...players].sort((a, b) => (numberOf(a.person_id) ?? 999) - (numberOf(b.person_id) ?? 999));
    const feed = [...actions].reverse().slice(0, 3).map(a => {
      const s = sentence(a, actions, { us: shortOf('us'), them: shortOf('them'), person }, R);
      return html`<li>${a.game_ms !== null && a.game_ms !== undefined ? `${fmtClock(a.game_ms)} · ` : ''}<b>${s.who}</b> · ${s.what}</li>`;
    });
    const who = (): Html => {
      if (!sel) return html`<span class="who"><b style="--c:#26292E;--ci:#8A919A">?</b><span>Touche un numéro<small>puis l’action</small></span></span>`;
      if (sel.side === 'us') { const p = person(sel.id), f = playerFouls(actions, sel.id, R);
        return html`<span class="who" style="${style('us')}"><b>${p.num ?? '–'}</b><span>${p.name ?? ''}<small>${f.total} faute${f.total > 1 ? 's' : ''}</small></span></span>`; }
      const f = playerFouls(actions, `#${sel.num}`, R);
      return html`<span class="who" style="${style('them')}"><b>${sel.num}</b><span>${shortOf('them')}<small>${f.total} faute${f.total > 1 ? 's' : ''}</small></span></span>`;
    };
    const dis = !sel || blocked() ? 'disabled' : '';
    return html`<div class="mq-land">
      ${landTop()}
      <div class="mq-m5">
        <section class="mq-side" style="${style('us')}">${sideHead('us')}
          <div class="mq-grid" data-guide="mq-grid">${ours.map(p => tile(numberOf(p.person_id), p.name, p.person_id, sel?.side === 'us' && sel.id === p.person_id, 'us', html`data-side="us" data-id="${p.person_id}"`))}</div></section>
        <section class="mq-ctr">
          <div class="mq-sc"><b>${sc.us}</b><span>–</span><b>${sc.them}</b></div>
          <div class="mq-clk5"><span class="q">${periodLabel(R, st.period)}</span>${R.clock ? clockOnly(now) : html`<b>—</b>`}
            ${st.common ? html`<span></span>` : html`<button type="button" class="mq-btn" data-a="clk" aria-label="${isRunning(st.clock) ? 'Arrêter le chrono' : st.phase === 'break' ? 'Passer la pause' : 'Lancer le chrono'}" ${R.clock ? '' : 'disabled'}>${ic(isRunning(st.clock) ? 'pause' : 'play')}</button>`}
            ${st.common ? html`<small>Chrono commun · géré par ${st.common.by}</small>` : st.phase === 'break' ? html`<small>${brkLabel()} · ▶ pour la passer</small>` : st.phase === 'end' ? html`<small>Temps réglementaire fini</small>` : ''}</div>
          <div class="mq-row2">${shotBox()}
            <button type="button" class="mq-poss" data-a="poss" aria-label="Flèche de possession"><span class="arr">${st.arrow === 'them' ? '▶' : st.arrow === 'us' ? '◀' : '⇄'}</span><small>Possession<b>${st.arrow ? shortOf(st.arrow) : 'à choisir'}</b></small></button></div>
          <div class="mq-tms">${(['us', 'them'] as Side[]).map(s => html`<button type="button" class="${st.to?.side === s ? 'run' : ''}" style="--c:${colorOf(s)}" data-a="to" data-side="${s}" ${toLeft(s) ? '' : 'disabled'}>
            <span data-to="${s}" data-idle="Temps-mort">${st.to?.side === s ? 'Temps-mort' : 'Temps-mort'}</span><small>${shortOf(s)} · ${toLeft(s)} restant${toLeft(s) > 1 ? 's' : ''}</small></button>`)}</div>
          <ol class="mq-feed" aria-live="polite">${feed}</ol>
        </section>
        <section class="mq-side" style="${style('them')}">${sideHead('them')}
          <div class="mq-grid">${oppNums().map(n => tile(n, null, `#${n}`, sel?.side === 'them' && sel.num === n, 'them', html`data-side="them" data-num="${n}"`))}
            <button type="button" class="mq-p add" data-a="addnum">+ N°</button></div></section>
      </div>
      ${(st.phase === 'end' || endArmed > Date.now()) && !st.finished ? html`<div class="mq-endbar"><span><b>${sc.us}–${sc.them}</b> · ${st.phase === 'end' ? 'temps réglementaire fini (rien d’automatique en 5x5)' : 'fin du match ?'}</span>
        <button type="button" class="mq-btn end" data-a="end">${endArmed > Date.now() ? `Confirmer ? ${sc.us}–${sc.them}` : 'Fin du match'}</button></div>` : ''}
      <div class="mq-act" style="${sel ? style(sel.side) : ''}" data-guide="mq-act">${who()}
        <button type="button" class="mq-a" data-a="act" data-k="1" ${dis}>+1</button><button type="button" class="mq-a" data-a="act" data-k="2" ${dis}>+2</button>
        <button type="button" class="mq-a" data-a="act" data-k="3" ${dis}>+3</button>
        <button type="button" class="mq-a fl" data-a="act" data-k="P" ${dis}>Faute<small>perso.</small></button>
        <button type="button" class="mq-a fl" data-a="act" data-k="T" ${dis}>Tech.<small>technique</small></button>
        <button type="button" class="mq-a fl" data-a="act" data-k="DI" ${dis}>Disrupt.<small>disruptive</small></button>
        <button type="button" class="mq-a fl" data-a="act" data-k="FL" ${dis}>Flagr.<small>flagrante</small></button>
        <button type="button" class="mq-a un" data-a="undo" ${actions.length ? '' : 'disabled'}>${ic('undo')} Annuler</button></div>
    </div>`;
  };
  /** Le temps seul (marqueur complet) : un toucher le corrige quand il est arrêté. */
  const clockOnly = (now: number): Html => {
    const editable = !st.common && !isRunning(st.clock) && st.phase !== 'break';
    return html`<b class="${st.phase === 'break' ? 'mq-brk' : ''} ${editable ? 'mq-edit' : ''}" data-clk ${editable ? html`data-a="cedit" role="button" tabindex="0" aria-label="Corriger le chrono"` : ''}>${fmtClock(clockLeft(now))}</b>`;
  };
  const shotBox = (): Html => {
    const R = r();
    if (st.common) return html`<span class="mq-sc24"><b>—</b><small>Temps courant<br>sans ${R.shot} s</small></span>`;
    if (!st.shotOn) return html`<button type="button" class="mq-sc24" data-a="shoton" aria-label="Allumer le chrono des tirs"><b>${R.shot}</b><small>${R.shot} s<br>option · éteint</small></button>`;
    return html`<div class="mq-sc24 on"><b data-shot ${isRunning(st.clock) ? '' : html`data-a="cedit" role="button" aria-label="Corriger les ${R.shot} s"`}>${fmtShot(leftOf(st.shot, T()))}</b><small>${R.shot} s<br>tirs</small>
      <span class="rs"><button type="button" data-a="sr" data-s="${R.shot}" aria-label="Changement de possession : remettre les ${R.shot} s">↻ ${R.shot}</button>${R.shotShort ? html`<button type="button" data-a="sr" data-s="${R.shotShort}" aria-label="Remettre ${R.shotShort} s">↻ ${R.shotShort}</button>` : ''}</span></div>`;
  };

  // 6 · Le marqueur 3x3, en paysage
  const drawFull3 = (): Html => {
    const R = r(), now = T(), sc = score(actions);
    const side = (s: Side) => {
      const tf = teamFouls(actions, R, st.period, s), badge = foulBadge(R, tf), left = timeoutsLeft(actions, R, st.period, s, 0);
      const k = s === 'us' ? { p1: 'us1', p2: 'us2', lf: 'us_ft', m: 'us_fix', to: 'us_to' } : { p1: 'opp1', p2: 'opp2', lf: 'opp_ft', m: 'opp_fix', to: 'opp_to' };
      return html`<section class="mq-h3" style="${style(s)}">
        <div class="mq-h3row"><span class="mq-ht"><span><b>${nameOf(s)}</b></span></span>
          <button type="button" class="mq-tm ${left ? '' : 'used'}" data-a="to" data-side="${s}" ${left || st.to?.side === s ? '' : 'disabled'}>${ic('clock')}<span data-to="${s}" data-idle="${left ? `TM ${R.timeoutSec} s` : 'TM pris'}">${left ? `TM ${R.timeoutSec} s` : 'TM pris'}</span></button></div>
        <div class="mq-score" aria-live="polite">${sc[s]}</div>
        <div class="mq-pts" ${s === 'us' ? html`data-guide="mq-pts3"` : ''}>
          <button type="button" data-a="pt" data-k="${k.p1}" ${blocked() ? 'disabled' : ''}>+1</button><button type="button" data-a="pt" data-k="${k.p2}" ${blocked() ? 'disabled' : ''}>+2</button>
          <button type="button" class="lf" data-a="pt" data-k="${k.lf}" ${blocked() ? 'disabled' : ''}>LF</button>
          <button type="button" class="minus" data-a="pt" data-k="${k.m}" ${blocked() || sc[s] <= 0 ? 'disabled' : ''} aria-label="Retirer un point à ${nameOf(s)}">−1</button></div>
        <button type="button" class="mq-f6" data-a="tf" data-side="${s}" aria-label="Une faute d’équipe de plus : ${nameOf(s)}"><span>Fautes</span>${pips(Math.min(tf, R.teamFouls), R.teamFouls, tf)}${badge ? html`<span class="mq-bonus">${badge}</span>` : ''}</button></section>`;
    };
    const feed = [...actions].reverse().slice(0, 3).map(a => { const s = sentence(a, actions, { us: shortOf('us'), them: shortOf('them'), person }, R); return html`<li><b>${s.who}</b> · ${s.what}</li>`; });
    return html`<div class="mq-land">
      ${landTop()}
      <div class="mq-m3">
        ${side('us')}
        <section class="mq-c3">
          <div class="big">${R.clock && st.period <= R.periods ? clockBox('', `${periodLabel(R, st.period, true)} · ${isRunning(st.clock) ? 'en cours' : 'arrêté'}`)
            : html`<b>${st.period > R.periods ? `${R.otTarget ?? 2} pts` : '—'}</b><small>${st.period > R.periods ? 'Prolongation : la première à 2 points' : `Sans chrono · ${R.target ?? 21} points`}</small>`}</div>
          ${st.common ? html`<span></span>` : st.shotOn ? html`<div class="mq-12w"><button type="button" class="mq-12" data-a="sr" data-s="${R.shot}" aria-label="Remettre les ${R.shot} s"><b data-shot>${fmtShot(leftOf(st.shot, now))}</b></button>
            <button type="button" class="mq-poss" data-a="sr" data-s="${R.shot}" aria-label="Changement de possession : remettre les ${R.shot} s">↻ Possession</button></div>`
            : html`<button type="button" class="mq-12 off" data-a="shoton" aria-label="Allumer les ${R.shot} s"><b>${R.shot} s</b><small>${R.shot} s<br>en option</small></button>`}
          ${R.clock && st.period <= R.periods && !st.common ? html`<button type="button" class="mq-dead ${isRunning(st.clock) ? '' : 'go'}" data-a="clk" data-guide="mq-dead">${isRunning(st.clock) ? html`${ic('pause')} Ballon mort` : st.phase === 'break' ? html`${ic('play')} Passer la pause` : html`${ic('play')} Jeu`}</button>` : html`<span></span>`}
          <ol class="mq-feed" aria-live="polite">${feed}</ol>
        </section>
        ${side('them')}
      </div>
      ${st.win ? winBar() : html`<div class="mq-endbar"><span>${netChip()}</span><button type="button" class="mq-btn" data-a="undo" ${actions.length ? '' : 'disabled'}>${ic('undo')} Annuler</button>
        <button type="button" class="mq-btn end" data-a="end">${endArmed > Date.now() ? `Confirmer ? ${sc.us}–${sc.them}` : 'Fin du match'}</button></div>`}
    </div>`;
  };
  const winBar = (): Html => {
    const sc = score(actions), w = st.win!;
    const why = st.period > r().periods ? `${r().otTarget ?? 2} points en prolongation : le match s’arrête.` : st.phase === 'end' ? 'Temps écoulé : l’équipe en tête gagne.' : `${r().target ?? 21} points, dans le temps : le match s’arrête.`;
    return html`<div class="mq-win" role="alertdialog" aria-label="Fin du match ?" data-guide="mq-win">${ic('trophy')}<span><b>Victoire ${deName(shortOf(w))} · ${sc[w]}–${sc[flip(w)]} ?</b><small>${why} Les nouveaux points attendent ta réponse.</small></span>
      <button type="button" class="no" data-a="win-no">Non, corriger</button><button type="button" class="yes" data-a="win-yes">Oui, fin du match</button></div>`;
  };

  // 9 · La TV : la recopie du téléphone de la table (sans réseau), aucun nom de joueuse
  const drawTv = (): Html => {
    const R = r(), sc = score(actions), now = T();
    const team = (s: Side) => {
      const tf = teamFouls(actions, R, st.period, s), badge = foulBadge(R, tf), left = timeoutsLeft(actions, R, st.period, s, leftOf(st.clock, now));
      const max = R.teamFouls;
      return html`<section class="mq-tvt" style="--c:${colorOf(s)}">
        <div class="nm">${shortOf(s)}</div>
        <div class="pts">${sc[s]}</div>
        <div class="ft"><div class="fl"><span>Fautes d’équipe${badge ? html`<span class="${R.format === '3x3' ? 'mq-bonus' : 'mq-pen'}">${badge}</span>` : ''}</span>${pips(Math.min(tf, max), max, tf)}</div>
          <div class="mq-tvto">${R.format === '3x3' ? html`<span class="${left ? '' : 'used'}"><i></i>Temps-mort</span>${left ? 'disponible' : 'pris'}`
            : html`<span class="${left ? '' : 'used'}"><i></i>Temps-morts</span>${left} restant${left > 1 ? 's' : ''}`}</div></div></section>`;
    };
    const pub = srv?.public_token ? `${location.origin}/m/${srv.public_token}` : null;
    return html`<div class="mq-tv" data-a="tvoff" aria-label="Affichage TV : toucher pour revenir à la table">
      <header class="mq-tvtop"><div class="l"><span class="lg">${o.logo ? html`<img src="${o.logo}" alt="">` : (o.us.name || 'A').charAt(0)}</span>
        <span><b>${o.kind === 'local' ? 'Table de marque' : titleOf()}</b><small>${o.kicker ?? 'Match'} · ${R.format} · ${periodLabel(R, st.period)}</small></span></div>
        ${ffbb()}<div class="r">${st.finished ? html`<span class="mq-chip"><i></i>Match terminé</span>` : html`<span class="mq-chip onair"><i></i>En direct</span>`}</div></header>
      <div class="mq-board">
        ${team('us')}
        <div class="mq-tvc"><div class="clk">${R.clock ? html`<b class="${st.phase === 'break' ? 'mq-brk' : ''}" data-clk>${fmtClock(clockLeft(now))}</b>` : html`<b>${periodLabel(R, st.period)}</b>`}
          <small>${st.phase === 'break' ? brkLabel() : R.format === '3x3' ? 'Temps de jeu' : periodLabel(R, st.period)}</small></div>
          ${st.shotOn ? html`<div class="sh"><b data-shot>${fmtShot(leftOf(st.shot, now))}</b><small>Tir · ${R.shot} s</small></div>` : html`<span></span>`}
          <p class="fmt">${R.format === '3x3' ? `${R.periodMin} min ou ${R.target ?? 21} points` : `${R.periods} × ${R.periodMin} min`}<br>Recopie du téléphone de la table</p></div>
        ${team('them')}
      </div>
      <div class="mq-tvb"><div class="nx"><span class="k">${st.win ? 'Fin du match ?' : st.finished ? 'Match terminé' : 'Score non officiel'}</span><b>${titleOf()}</b><small>Aucun nom de ${playerNoun(o.gender ?? 'X')} à l’écran : des scores et des fautes d’équipe.</small></div>
        ${pub ? html`<div class="qr"><span class="q">${raw(qrSvg(pub, 'Suivre ce match'))}</span><span><b>Suivre ce match</b><small>sur ton téléphone, sans compte</small></span></div>` : ''}</div>
    </div>`;
  };

  const draw = () => {
    if (closed) return;
    const view: View = st.view;
    host.classList.toggle('mq-tel', ['setup', 'other', 'lost', 'done', 'closed', 'pass', 'loading'].includes(screen));
    let body: Html;
    if (screen === 'loading') body = html`<div class="mq-tel-in"><p class="mq-note">Ouverture de la table…</p></div>`;
    else if (screen === 'setup') body = drawSetup();
    else if (screen === 'other') body = drawOther();
    else if (screen === 'lost') body = drawLost();
    else if (screen === 'closed') body = drawClosed();
    else if (screen === 'done') body = drawDone();
    else if (screen === 'pass') body = drawPass();
    else if (screen === 'tv') body = drawTv();
    else body = view === 'simple' ? drawSimple() : r().format === '3x3' ? drawFull3() : drawFull5();
    mount(host, body);
    paintClocks();
  };

  // --- Les touchers ---------------------------------------------------------------------------------------------------------
  const ACT: Record<string, [ActionKind, ActionKind]> = { 1: ['ft', 'opp1'], 2: ['p2', 'opp2'], 3: ['p3', 'opp3'], P: ['foul', 'opp_foul'], T: ['foul_t', 'opp_foul_t'], DI: ['foul_di', 'opp_foul_di'], FL: ['foul_fl', 'opp_foul_fl'] };
  host.addEventListener('click', e => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-a]');
    if (!b || (b as HTMLButtonElement).disabled) return;
    const a = b.dataset.a!;
    const now = T();
    switch (a) {
      case 'back': close(); o.onClose(); return;
      case 'preset': setup = { ...setup, preset: b.dataset.p! }; draw(); return;
      case 'sview': setup = { ...setup, view: b.dataset.v === 'full' ? 'full' : 'simple' }; draw(); return;
      case 'open': void openIt(); return;
      case 'ask': void askIt(); return;
      case 'mode': if (o.onMode) { close(); o.onMode(b.dataset.m === 'score' ? 'score' : 'full'); } return;
      case 'pt': add(b.dataset.k as ActionKind); return;
      case 'tf': if (!st.finished) add(b.dataset.side === 'them' ? 'opp_foul' : 'us_foul'); return;
      case 'undo': undo(); return;
      case 'qt': change(setPeriod(st, st.period + Number(b.dataset.d), now)); return;
      case 'clk': change(toggleClock(st, now)); return;
      case 'shoton': change({ ...resetShot({ ...st, shotOn: true }, r().shot, now) }); toast(`Chrono des tirs : ${r().shot} s allumé (réglages pour l’éteindre)`); return;
      case 'sr': change(resetShot(st, Number(b.dataset.s), now)); return;
      case 'poss': change({ ...st, arrow: st.arrow ? flip(st.arrow) : 'us' }); return;
      case 'to': {
        const s = b.dataset.side as Side;
        if (st.common) { toast('Chrono commun : pas de temps-mort à la table.', 'ko'); return; }
        if (st.to?.side === s) { change({ ...st, to: null }); return; }        // fin du temps-mort avant la minute
        if (timeoutsLeft(actions, r(), st.period, s, leftOf(st.clock, now)) <= 0) { toast('Plus de temps-mort pour cette période', 'ko'); return; }
        st = takeTimeout(st, s, now);
        add(s === 'us' ? 'us_to' : 'opp_to');
        return;
      }
      case 'pick': {
        sel = b.dataset.side === 'them' ? { side: 'them', num: Number(b.dataset.num) } : { side: 'us', id: b.dataset.id! };
        draw(); return;
      }
      case 'act': {
        if (!sel) { toast('Touche d’abord un numéro.', 'ko'); return; }
        const [ku, kt] = ACT[b.dataset.k!]!;
        if (sel.side === 'us') add(ku, { person_id: sel.id }); else add(kt, { opp_number: sel.num });
        const f = sel.side === 'us' ? playerFouls(actions, sel.id, r()) : playerFouls(actions, `#${sel.num}`, r());
        if (/^[PTDF]/.test(b.dataset.k!) && (f.gd || (f.out && f.total === (r().playerFouls ?? 5)))) toast(f.gd ? 'Exclusion : 2 fautes techniques ou flagrantes (règles FIBA 2026)' : `${f.total} fautes : sortie`, 'ko');
        return;
      }
      case 'addnum': askNumber(); return;
      case 'end':
        if (endArmed > Date.now()) { endArmed = 0; finish(); return; }
        endArmed = Date.now() + 4000; draw(); setTimeout(() => { if (endArmed && endArmed <= Date.now()) { endArmed = 0; draw(); } }, 4100); return;
      case 'win-yes': finish(); return;
      case 'win-no': change({ ...st, win: null }, false); toast('Corrige avec « Annuler » ou « −1 » : la question revient à 21.'); return;
      case 'set': openSettings(); return;
      case 'cedit':
        if (st.common) { toast(`Chrono commun : géré par ${st.common.by}`); return; }
        if (isRunning(st.clock)) { toast('Arrête le chrono pour le corriger.', 'ko'); return; }
        openClockEdit(); return;
      case 'tvoff': screen = back; draw(); return;
      case 'board': screen = 'board'; draw(); return;
      case 'pass': void passIt(); return;
      case 'new': dropLocal('local'); st = newState(st.preset, now, { view: st.view, rules: st.rules }); st = { ...st, names: st.names, colors: st.colors }; actions = []; queue = []; screen = 'board'; persist(); draw(); return;
      case 'sheet': close(); return;
      default: return;
    }
  });

  const nameInput = () => (host.querySelector<HTMLInputElement>('#mq-name')?.value ?? '').trim();
  const openIt = async () => {
    const st0 = newState(setup.preset, T(), { view: setup.view, rules: o.rules });
    if (o.kind === 'local') { st = st0; actions = []; queue = []; screen = 'board'; persist(); draw(); return; }
    o.onView?.(setup.view);
    const name = nameInput();
    saveName(name); setup = { ...setup, name };
    const btn = host.querySelector<HTMLButtonElement>('[data-a=open]');
    if (btn) btn.disabled = true;
    try {
      const s = await api.open(key, name || null, st0);
      if (s.mine) { st = st0; adopt(s, true); st = { ...st, view: setup.view }; persist(); kick(0); }
      else { srv = s; screen = 'other'; startPoll(4000); }
    } catch (e) {
      toast(e instanceof TableError && e.kind === 'closed' ? 'La table s’ouvre le jour du match, jusqu’au score final.' : e instanceof TableError && e.kind === 'denied' ? 'Réservé à la saisie de ce match.' : 'Pas de réseau : la table s’ouvre une première fois avec le réseau.', 'ko');
    }
    draw();
  };
  const askIt = async () => {
    const name = nameInput();
    saveName(name); setup = { ...setup, name };
    try {
      const s = await api.ask(key, name || null);
      if (s.mine) { adopt(s, true); toast('La table était libre : tu la tiens'); }
      else { asking = true; toast(o.backend ? 'Demande envoyée à l’organisation' : 'Demande envoyée au staff du match'); }
    } catch { toast('Demande impossible pour le moment.', 'ko'); }
    draw();
  };
  const passIt = async () => {
    if (!sender) return;
    if (queue.length) await sender.flush();
    if (queue.length) { toast('Passer la main demande le réseau : les actions en attente doivent partir d’abord.', 'ko'); return; }
    screen = 'pass'; passToken = null; draw();
    try {
      passToken = await api.pass(key);
      passUntil = Date.now() + 120_000;
      startPoll(2000);
    } catch { screen = 'board'; toast('QR impossible pour le moment (réseau ?).', 'ko'); }
    draw();
  };
  const askNumber = () => openSheet('Un numéro adverse', html`
    <form class="form" id="mq-num" novalidate><label class="field"><span>Numéro (0 à 99)</span><input type="number" inputmode="numeric" min="0" max="99" name="n" required></label>
      <p class="form-msg" role="alert"></p><button class="btn btn-primary" type="submit">Ajouter</button></form>`, (sheet, closeSheet) => {
    sheet.parentElement!.classList.add('mq-over');
    const f = sheet.querySelector<HTMLFormElement>('#mq-num')!;
    f.addEventListener('submit', ev => {
      ev.preventDefault();
      const n = Number(new FormData(f).get('n'));
      if (!Number.isInteger(n) || n < 0 || n > 99) { f.querySelector('.form-msg')!.textContent = 'Un numéro de 0 à 99.'; return; }
      st = { ...st, oppNums: [...new Set([...st.oppNums, n])].slice(0, 20) };
      sel = { side: 'them', num: n };
      closeSheet(); persist(); kick(400); draw();
    });
  });

  /** Corriger le chrono arrêté : −10 s, −1 s, +1 s, +10 s ou mm:ss ; la période ; les 24 s / 12 s allumées. */
  const openClockEdit = () => {
    const R = r(), now = T();
    let left = Math.round(leftOf(st.clock, now) / 1000), period = st.period, shot = Math.ceil(leftOf(st.shot, now) / 1000);
    const mmss = (sec: number) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
    const maxSec = Math.round(periodMs(R, period) / 1000);
    openSheet('Corriger le chrono', html`
      <div class="mq-ce" data-guide="mq-cedit">
        <div class="mq-ce-row"><span>Période</span><div class="mq-qt"><button type="button" data-e="q" data-d="-1" aria-label="Période précédente">‹</button><b data-v="q">${periodLabel(R, period)}</b><button type="button" data-e="q" data-d="1" aria-label="Période suivante">›</button></div></div>
        <label class="field"><span>Temps restant (mm:ss)</span><input id="mq-ce-t" inputmode="numeric" maxlength="5" value="${mmss(left)}" autocomplete="off"></label>
        <div class="mq-ce-steps">${[-10, -1, 1, 10].map(d => html`<button type="button" class="btn btn-ghost btn-sm" data-e="t" data-d="${d}">${d > 0 ? '+' : '−'}${Math.abs(d)} s</button>`)}</div>
        ${st.shotOn ? html`<label class="field"><span>Chrono des tirs (s)</span><input id="mq-ce-s" type="number" inputmode="numeric" min="0" max="${R.shot}" value="${shot}"></label>
          <div class="mq-ce-steps">${[-1, 1].map(d => html`<button type="button" class="btn btn-ghost btn-sm" data-e="s" data-d="${d}">${d > 0 ? '+' : '−'}1 s</button>`)}</div>` : ''}
        <p class="hint">Chaque correction est notée au journal et sur la feuille de match du club.</p>
        <button type="button" class="btn btn-primary" data-e="ok">Enregistrer</button></div>`, (sheet, closeSheet) => {
      sheet.parentElement!.classList.add('mq-over');
      const tIn = sheet.querySelector<HTMLInputElement>('#mq-ce-t')!, sIn = sheet.querySelector<HTMLInputElement>('#mq-ce-s');
      const parse = (v: string) => { const m = /^\s*(\d{1,2})(?::(\d{1,2}))?\s*$/.exec(v); return m ? (m[2] !== undefined ? Number(m[1]) * 60 + Number(m[2]) : Number(m[1]) * 60) : null; };
      sheet.addEventListener('click', ev => {
        const b = (ev.target as HTMLElement).closest<HTMLElement>('[data-e]');
        if (!b) return;
        const d = Number(b.dataset.d ?? 0);
        if (b.dataset.e === 'q') { period = Math.min(8, Math.max(1, period + d)); sheet.querySelector('[data-v=q]')!.textContent = periodLabel(R, period); if (period !== st.period) { left = Math.round(periodMs(R, period) / 1000); tIn.value = mmss(left); } }
        else if (b.dataset.e === 't') { left = Math.min(Math.max(maxSec, left), Math.max(0, (parse(tIn.value) ?? left) + d)); tIn.value = mmss(left); }
        else if (b.dataset.e === 's' && sIn) { shot = Math.min(R.shot, Math.max(0, Number(sIn.value || shot) + d)); sIn.value = String(shot); }
        else if (b.dataset.e === 'ok') {
          const t = parse(tIn.value);
          if (t === null || t > 60 * 60) { toast('Le temps : mm:ss (ex. 7:00)', 'ko'); return; }
          const sh = sIn ? Math.min(R.shot, Math.max(0, Number(sIn.value))) : undefined;
          const res = fixClock(st, { period, left: t * 1000, shot: sh !== undefined ? sh * 1000 : undefined }, T());
          closeSheet();
          if (!res.fixes.length) return;
          st = res.s;
          for (const f of res.fixes) addFix(f);
          persist(); kick(); draw();
          toast('Chrono corrigé (noté au journal)');
        }
      });
    });
  };

  const openSettings = () => {
    const R = r();
    const num = (k: keyof Rules, label: string, min: number, max: number) => html`<label class="field"><span>${label}</span><input type="number" inputmode="numeric" min="${min}" max="${max}" data-r="${k}" value="${String(R[k] ?? '')}"></label>`;
    const pub = srv?.public_token ? `${location.origin}/m/${srv.public_token}` : null;
    openSheet('Réglages de la table', html`
      ${o.onMode ? html`<div class="sheet-sec"><h4>Saisie</h4><div class="seg"><button type="button" data-s="mode-full">Stats</button><button type="button" data-s="mode-score">Score seul</button><button type="button" aria-pressed="true">Table de marque</button></div></div>` : ''}
      ${fullOk ? html`<div class="sheet-sec"><h4>La table</h4><div class="seg"><button type="button" data-s="view-simple" aria-pressed="${String(st.view === 'simple')}">Table simple</button><button type="button" data-s="view-full" aria-pressed="${String(st.view === 'full')}">${R.format === '3x3' ? 'Marqueur 3x3' : 'Marqueur complet'}</button></div></div>` : ''}
      <div class="btn-row"><button type="button" class="btn btn-ghost btn-sm" data-s="tv">${ic('tv')} Affichage TV</button>
        ${o.kind === 'match' ? html`<button type="button" class="btn btn-ghost btn-sm" data-s="pass">${ic('hand')} Passer la main</button>` : html`<button type="button" class="btn btn-ghost btn-sm" data-s="new">Nouveau match</button>`}
        ${st.view === 'full' && R.format === '5x5' && !st.finished ? html`<button type="button" class="btn btn-danger-ghost btn-sm" data-s="end">Fin du match</button>` : ''}</div>
      ${pub ? html`<div class="sheet-sec"><h4>Le suivi des parents (sans compte)</h4><p class="hint">Le score, le quart-temps, le chrono et les fautes d’équipe ; aucun nom.</p>
        <div class="btn-row"><button type="button" class="btn btn-ghost btn-sm" data-s="copy">Copier le lien</button>${'share' in navigator ? html`<button type="button" class="btn btn-ghost btn-sm" data-s="share">Partager</button>` : ''}</div></div>` : ''}
      <div class="sheet-sec"><h4>Les règles · ${presetOf(st.preset).label}</h4>
        <div class="mq-presets">${PRESETS.map(x => html`<button type="button" data-s="preset" data-p="${x.id}" aria-pressed="${String(x.id === st.preset)}"><b>${x.label}</b><small>${x.sub}</small></button>`)}</div>
        <form class="form" id="mq-rules" novalidate>
          ${R.format === '5x5' ? html`${num('periods', 'Quarts-temps', 1, 4)}${num('periodMin', 'Minutes par quart-temps', 1, 20)}${num('otMin', 'Minutes de prolongation', 1, 10)}
            ${num('breakMin', 'Pause entre deux quarts-temps (min)', 0, 15)}${num('halfMin', 'Mi-temps (min)', 0, 30)}${num('teamFouls', 'Pénalité après ces fautes d’équipe', 1, 10)}${num('playerFouls', 'Fautes pour sortir', 1, 10)}`
          : html`${num('periodMin', 'Minutes de jeu', 1, 30)}${num('target', 'Points pour gagner dans le temps', 5, 50)}${num('teamFouls', 'Fautes d’équipe avant le bonus', 1, 15)}`}
          <label class="check"><input type="checkbox" data-c="clock" ${R.clock ? 'checked' : ''}><span>Chrono de match</span></label>
          <label class="check"><input type="checkbox" data-c="shotOn" ${st.shotOn ? 'checked' : ''}><span>Chrono des tirs (${R.shot} s), éteint par défaut</span></label>
          <button class="btn btn-primary btn-sm" type="submit">Appliquer</button></form>
        <p class="hint">Règles FIBA 2026 par défaut, toutes réglables. Hors compétition FFBB.</p></div>
      <div class="sheet-sec"><h4>${o.kind === 'local' ? 'Les couleurs' : 'La couleur de l’adversaire'}</h4>
        ${(o.kind === 'local' ? (['us', 'them'] as Side[]) : (['them'] as Side[])).map(sd => html`<div class="mq-colrow"><span>${nameOf(sd)}</span><div class="btn-row">${PALETTE.map(c => html`<button type="button" class="btn btn-sm mq-sw" style="background:${c};color:${inkOn(c)}" data-s="color" data-side="${sd}" data-c="${c}" aria-pressed="${String(colorOf(sd).toLowerCase() === c.toLowerCase())}" aria-label="${nameOf(sd)} en ${COLOR_LABEL[c]?.[0] ?? c}">●</button>`)}</div></div>`)}
        ${o.kind === 'local' ? html`<p class="hint">Une équipe nommée d’après sa couleur change de nom avec elle.</p>` : ''}</div>
      ${o.kind === 'local' ? html`<div class="sheet-sec"><h4>Les équipes</h4><label class="field"><span>Équipe 1</span><input data-n="us" maxlength="30" value="${nameOf('us')}"></label>
        <label class="field"><span>Équipe 2</span><input data-n="them" maxlength="30" value="${nameOf('them')}"></label></div>` : ''}`, (sheet, closeSheet) => {
      sheet.parentElement!.classList.add('mq-over');
      sheet.addEventListener('click', ev => {
        const b = (ev.target as HTMLElement).closest<HTMLElement>('[data-s]');
        if (!b) return;
        const s = b.dataset.s!, now = T();
        if (s === 'mode-full' || s === 'mode-score') { closeSheet(); close(); o.onMode?.(s === 'mode-score' ? 'score' : 'full'); return; }
        if (s === 'view-simple' || s === 'view-full') { closeSheet(); change({ ...st, view: s === 'view-full' ? 'full' : 'simple' }, false); o.onView?.(st.view); return; }
        if (s === 'tv') { closeSheet(); back = 'board'; screen = 'tv'; draw(); return; }
        if (s === 'end') { closeSheet(); endArmed = Date.now() + 4000; draw(); toast('Touche « Fin du match » encore une fois pour confirmer'); return; }
        if (s === 'pass') { closeSheet(); void passIt(); return; }
        if (s === 'new') { closeSheet(); dropLocal('local'); st = newState(st.preset, now, { view: st.view, rules: st.rules }); actions = []; screen = 'board'; persist(); draw(); return; }
        if (s === 'copy' && pub) { void navigator.clipboard?.writeText(pub).then(() => toast('Lien copié'), () => toast(pub)); return; }
        if (s === 'share' && pub) { void navigator.share({ title: titleOf(), text: 'Le match en direct (score, quart-temps, chrono) :', url: pub }).catch(() => undefined); return; }
        if (s === 'preset') {
          const p = presetOf(b.dataset.p!);
          if (actions.length && p.rules.format !== r().format) { toast('Le match a commencé : pas de changement entre 3x3 et 5x5.', 'ko'); return; }
          closeSheet();
          const fresh = newState(p.id, now, { view: st.view });
          change({ ...st, preset: p.id, rules: fresh.rules, clock: actions.length || st.clock.start !== null ? st.clock : fresh.clock, shot: fresh.shot });
          return;
        }
        if (s === 'color') {
          closeSheet();
          const sd: Side = b.dataset.side === 'us' ? 'us' : 'them', c = b.dataset.c!;
          const cur = sd === 'us' ? st.names.us ?? o.us.name : st.names.them ?? o.them.name;
          const names = o.kind === 'local' && COLOR_TEAMS.has(cur) && COLOR_LABEL[c] ? { ...st.names, [sd]: COLOR_LABEL[c][1] } : st.names;
          change({ ...st, colors: { ...st.colors, [sd]: c }, names }); return;
        }
      });
      sheet.querySelector<HTMLFormElement>('#mq-rules')?.addEventListener('submit', ev => {
        ev.preventDefault();
        const R2: Rules = { ...r() };
        for (const inp of sheet.querySelectorAll<HTMLInputElement>('[data-r]')) {
          const v = Math.round(Number(inp.value));
          if (!Number.isFinite(v) || v < Number(inp.min) || v > Number(inp.max)) { toast(`${inp.closest('label')?.querySelector('span')?.textContent ?? 'Valeur'} : de ${inp.min} à ${inp.max}`, 'ko'); return; }
          (R2 as unknown as Record<string, number>)[inp.dataset.r!] = v;
        }
        R2.clock = !!sheet.querySelector<HTMLInputElement>('[data-c=clock]')?.checked;
        const shotOn = !!sheet.querySelector<HTMLInputElement>('[data-c=shotOn]')?.checked;
        const fresh = st.clock.start === null && st.clock.fix === 0 && st.phase === 'play';
        let next: TableState = { ...st, rules: R2, shotOn: shotOn && !st.common };
        if (fresh) next = { ...next, clock: { ...st.clock, dur: periodMs(R2, st.period) } };
        for (const inp of sheet.querySelectorAll<HTMLInputElement>('[data-n]')) next = { ...next, names: { ...next.names, [inp.dataset.n as Side]: inp.value.trim() || null } };
        closeSheet(); change(next); toast('Réglages appliqués');
      });
    });
  };

  draw();
  void load();
  return close;
}

/** Une couleur de club sûre pour un attribut style. */
export const cssColor = (c: string | null | undefined, d: string) => (c && /^#[0-9a-fA-F]{6}$/.test(c) ? c : d);

/** La base injoignable au rechargement (l'app ne peut pas se charger) : la table de ce téléphone se rouvre quand même,
 *  avec son journal et sa file d'envoi (D-176, « Hors réseau ») ; tout part au retour du réseau. */
export function offlineResume(root: HTMLElement, where: { event?: string; link?: string }): boolean {
  const ev = where.event ?? (where.link ? linkEvent(where.link) : null);
  const l = ev ? loadLocal(ev) : null;
  if (!ev || !l?.ctx || l.state.finished && !l.queue.length) return false;
  const c = l.ctx as Pick<TableOpts, 'us' | 'them' | 'kicker' | 'gender' | 'isHome' | 'players' | 'numbers' | 'logo' | 'link'>;
  const b = document.createElement('button');
  b.type = 'button'; b.className = 'btn btn-primary'; b.textContent = 'Reprendre la table de marque (hors réseau)';
  b.addEventListener('click', () => { openTable({ kind: 'match', eventId: ev, ...c, players: c.players ?? [], numbers: c.numbers ?? {}, onClose: () => location.reload() }); });
  (root.querySelector('.empty') ?? root).append(b);
  return true;
}
